import { useEffect } from 'react';
import { useCustomerBranding } from '@/hooks/use-customer-branding';
import { DEFAULT_BRAND_NAME } from '@/hooks/use-tenant-branding';
import { useCustomerAuthStore } from '@/stores/customer-auth.store';

const DEFAULT_FAVICON = '/favicon.ico';
const MANAGED_ICON_ID = 'tenant-favicon';

/**
 * Applies the tenant's branding to the Customer Portal document head.
 *
 * Mirrors the staff-side TenantBrandingEffect, including the isolation rule
 * that motivated it: react-query keeps cached data after logout, so keying on
 * the branding data alone would leave one tenant's name and favicon in place
 * for whoever signs in next on the same browser. Keying on `isAuthenticated`
 * forces a reset to the product default the moment the session ends.
 *
 * It reuses the same managed `<link>` id as the staff effect. Only one of the
 * two is ever mounted — App.tsx picks a single branch per page load — so they
 * cannot fight over the element, and sharing the id means a stale icon from one
 * portal is replaced rather than duplicated by the other.
 */
export function CustomerBrandingEffect() {
  const isAuthenticated = useCustomerAuthStore((s) => s.isAuthenticated);
  const { brandName, faviconUrl } = useCustomerBranding();

  const activeName = isAuthenticated ? brandName : DEFAULT_BRAND_NAME;
  const activeIcon = isAuthenticated ? faviconUrl : null;

  useEffect(() => {
    document.title = activeName;
  }, [activeName]);

  useEffect(() => {
    let link = document.getElementById(MANAGED_ICON_ID) as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.id = MANAGED_ICON_ID;
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = activeIcon ?? DEFAULT_FAVICON;
  }, [activeIcon]);

  return null;
}
